import React, { useEffect, useMemo, useState } from "react";
import "./caseAssignmentForm.scss";
import { CaseDetail } from "../../../../models/CaseDetail/caseDetail.model";
import { Form, Formik } from "formik";
import { Button, Col, Row, Switch } from "antd";
import DropdownField from "../../../../shared/components/DropdownField";
import { IDropdownOptions } from "../../../../shared/utils/dropdownUtils";
import DoctorService from "../../../../services/Doctor/doctor.service";
import { Doctor } from "../../../../models/Doctor/doctor.model";
import { Admin } from "../../../../models/Admin/admin.model";
import AdminCaseService from "../../../../services/Case/AdminCase/adminCase.service";
import { UserRoleEnum } from "../../../../enums/userRole.enum";
import { CaseStatusEnum } from "../../../../enums/caseStatus.enum";
import AuthContainer from "../../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../../store/reducers/authReducer";
import DoctorCaseService from "../../../../services/Case/DoctorCase/doctorCase.service";
import ScanCenterCaseService from "../../../../services/Case/ScanCenterCase/scanCenterCase.service";
import { DoctorFeatureEnum } from "../../../../enums/DoctorFeature.enum";
import { ScanCenter } from "../../../../models/ScanCenter/scanCenter.model";

function numAssignmentId(v: string | number | undefined | null): number | null {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) || n <= 0 ? null : n;
}

/** API does not send self_assign; infer from assignment row (admin/doctor self-assign). */
function isAssignmentToSelf(
  caseDetail: CaseDetail,
  user: Admin | Doctor | ScanCenter | undefined,
  userRole: UserRoleEnum | undefined
): boolean {
  if (!user || !userRole || userRole === UserRoleEnum.SCAN_CENTER) return false;

  if (userRole === UserRoleEnum.ADMIN) {
    const uid = (user as Admin).id;
    if (uid === undefined || uid === null) return false;
    const assignedAdmin = numAssignmentId(caseDetail.assignedToAdminId);
    const assignedDoc = numAssignmentId(caseDetail.assignedToDoctorId);
    return (
      assignedAdmin !== null &&
      assignedAdmin === Number(uid) &&
      assignedDoc === null
    );
  }

  if (userRole === UserRoleEnum.DOCTOR) {
    const uid = (user as Doctor).id;
    if (uid === undefined || uid === null) return false;
    const assignedDoc = numAssignmentId(caseDetail.assignedToDoctorId);
    const assignedAdmin = numAssignmentId(caseDetail.assignedToAdminId);
    return (
      assignedDoc !== null &&
      assignedDoc === Number(uid) &&
      assignedAdmin === null
    );
  }

  return false;
}

function buildAssignmentDoctorGroupedOptions(doctors: Doctor[]) {
  const direct = doctors.filter(
    (d) => d.type === UserRoleEnum.DOCTOR && !d.assignmentGroupLabel
  );
  const groupsMap = new Map<string, Doctor[]>();
  doctors.forEach((d) => {
    if (d.type === UserRoleEnum.DOCTOR && d.assignmentGroupLabel) {
      const list = groupsMap.get(d.assignmentGroupLabel) ?? [];
      list.push(d);
      groupsMap.set(d.assignmentGroupLabel, list);
    }
  });
  const admins = doctors.filter((d) => d.type === UserRoleEnum.ADMIN);
  const grouped: Array<{
    label: string;
    options: Array<{ label: string; value: string; doctor: Doctor }>;
  }> = [];
  if (direct.length) {
    grouped.push({
      label: "Direct doctors",
      options: direct.map((d) => ({
        label: d.doctorName ?? "",
        value: `doctor-${d.id}`,
        doctor: d,
      })),
    });
  }
  if (admins.length) {
    grouped.push({
      label: "Telerad administrators",
      options: admins.map((a) => ({
        label: a.doctorName ?? "",
        value: `admin-${a.id}`,
        doctor: a,
      })),
    });
  }
  groupsMap.forEach((list, label) => {
    grouped.push({
      label,
      options: list.map((d) => ({
        label: d.doctorName ?? "",
        value: `doctor-${d.id}`,
        doctor: d,
      })),
    });
  });
  return grouped;
}

interface CaseAssignmentFormProps extends AuthReducerProps {
  caseDetail: CaseDetail;
  onSuccess: (caseDetail: CaseDetail) => void;
}

function CaseAssignmentForm({
  caseDetail,
  onSuccess,
  userRole,
  user
}: CaseAssignmentFormProps) {

  const doctorFeatures = useMemo(() => {
    return user?.doctorFeatures?.map((doctorFeature) => doctorFeature.feature) ?? [];
  }, [user, userRole]);

  const hasTypistLogin = useMemo(() => {
    return doctorFeatures.includes(DoctorFeatureEnum.TYPIST_LOGIN);
  }, [doctorFeatures]);

  const initialFormValues = useMemo(() => {
    const merged = Object.assign(new CaseDetail(), caseDetail);
    const inferredSelf =
      Boolean(caseDetail.selfAssign) || isAssignmentToSelf(caseDetail, user, userRole);
    merged.selfAssign = inferredSelf;
    return merged;
  }, [caseDetail, user, userRole]);

  const [formLoading, setFormLoading] = useState(false);

  const [removeAssignmentLoading, setRemoveAssignmentLoading] = useState(false);

  const [doctorOptions, setDoctorOptions] = useState<IDropdownOptions[]>([]);

  /**
   * Compute the Select's currently-selected option value using the same
   * precedence the Select uses: admin id first, then doctor id.
   */
  const selectedAssigneeValue = useMemo(() => {
    const adminId = numAssignmentId(caseDetail?.assignedToAdminId);
    if (adminId !== null) return `admin-${adminId}`;
    const docId = numAssignmentId(caseDetail?.assignedToDoctorId);
    if (docId !== null) return `doctor-${docId}`;
    return undefined;
  }, [caseDetail?.assignedToAdminId, caseDetail?.assignedToDoctorId]);

  /**
   * When the active assignee is not present in the loaded options (e.g. a
   * scan center viewing a case assigned to a doctor it cannot reach — the
   * backend surfaces the doctor's telerad admin as the display label),
   * AntD Select falls back to rendering the raw option value like
   * "doctor-6". Prepend a display-only group with the backend-provided
   * `assignedToDoctorName` so the current assignee renders as a name.
   */
  const displayDoctorOptions = useMemo(() => {
    if (!selectedAssigneeValue) return doctorOptions;
    const allValues = new Set<string>();
    doctorOptions.forEach((group: any) => {
      (group?.options ?? []).forEach((opt: any) => {
        if (opt?.value) allValues.add(opt.value);
      });
    });
    if (allValues.has(selectedAssigneeValue)) return doctorOptions;
    const fallbackLabel =
      (caseDetail as any)?.assignedToAdminName ||
      caseDetail?.assignedToDoctorName;
    if (!fallbackLabel) return doctorOptions;
    return [
      {
        label: "Current assignee",
        options: [
          {
            label: fallbackLabel,
            value: selectedAssigneeValue,
            disabled: true,
          },
        ],
      },
      ...doctorOptions,
    ];
  }, [
    doctorOptions,
    selectedAssigneeValue,
    caseDetail?.assignedToDoctorName,
    (caseDetail as any)?.assignedToAdminName,
  ]);

  const handleSubmit = (values: CaseDetail) => {
    const caseDetail = Object.assign(new CaseDetail(), values);
    setFormLoading(true);
    if (userRole === UserRoleEnum.SCAN_CENTER) {
      ScanCenterCaseService.assignCase(
        caseDetail,
        (updated: CaseDetail) => {
          onSuccess(updated);
        },
        () => {},
        () => {
          setFormLoading(false);
        }
      );
      return;
    }
    const API_URL =
      userRole === UserRoleEnum.ADMIN
        ? AdminCaseService.assignCase
        : DoctorCaseService.assignCase;
    API_URL(
      caseDetail.selfAssign,
      caseDetail,
      (caseDetail: CaseDetail) => {
        onSuccess(caseDetail);
      },
      () => {},
      () => {
        setFormLoading(false);
      }
    );
  };

  const handleRemoveAssignment = () => {
    if (
      !caseDetail.id ||
      (userRole !== UserRoleEnum.ADMIN && userRole !== UserRoleEnum.SCAN_CENTER)
    )
      return;
    setRemoveAssignmentLoading(true);
    const removeAssignmentApi =
      userRole === UserRoleEnum.SCAN_CENTER
        ? ScanCenterCaseService.removeAssignment
        : AdminCaseService.removeAssignment;
    removeAssignmentApi(
      caseDetail.id,
      (caseDetail: CaseDetail) => {
        onSuccess(caseDetail);
      },
      () => {},
      () => {
        setRemoveAssignmentLoading(false);
      }
    );
  };

  useEffect(() => {
    if(userRole) {
      DoctorService.fetchAssignmentDoctors(
        userRole as UserRoleEnum,
        (doctors: Doctor[]) => {
          setDoctorOptions(buildAssignmentDoctorGroupedOptions(doctors));
        },
        () => {},
        () => {}
      );
    }
  }, [userRole]);

  return (
    <div className="case-assignment-form">
      <Formik
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={null}
      >
        {({ values, isValid, setFieldValue }) => {
          return (
            <Form>
              <Row gutter={[0, 20]}>
                {((userRole === UserRoleEnum.ADMIN &&
                  caseDetail?.status === CaseStatusEnum.ASSIGNED) ||
                  (userRole === UserRoleEnum.SCAN_CENTER &&
                    (caseDetail?.caseStatus === CaseStatusEnum.ASSIGNED ||
                      caseDetail?.caseStatus ===
                        CaseStatusEnum.TO_BE_APPROVED))) && (
                  <Col span={24}>
                    <Button
                      className="case-assignment-form__remove-assignment-button"
                      danger
                      disabled={removeAssignmentLoading}
                      loading={removeAssignmentLoading}
                      onClick={handleRemoveAssignment}
                    >
                      Remove assignment
                    </Button>
                  </Col>
                )}
                {!hasTypistLogin &&
                  userRole !== UserRoleEnum.SCAN_CENTER && (
                    <>
                      <Col
                        span={24}
                        className="case-assignment-form__toggle-wrapper"
                      >
                        <span>Assign to doctor</span>
                        <Switch
                          checked={values?.selfAssign}
                          onChange={(checked) => {
                            setFieldValue("selfAssign", checked);
                            setFieldValue("assignedToDoctorId", undefined);
                            setFieldValue("assignedToAdminId", undefined);
                          }}
                        />
                        <span>Self Assign</span>
                      </Col>
                    </>
                  )}
                {(userRole === UserRoleEnum.SCAN_CENTER ||
                  !values?.selfAssign) && (
                  <Col span={24}>
                    <DropdownField
                      placeHolder="Select Doctor"
                      showSearch
                      title="Doctor"
                      name="assignedToDoctorId"
                      optionLabelProp="label"
                      value={(() => {
                        const adminId = numAssignmentId(values.assignedToAdminId);
                        if (adminId !== null) return `admin-${adminId}`;
                        const docId = numAssignmentId(values.assignedToDoctorId);
                        if (docId !== null) return `doctor-${docId}`;
                        return undefined;
                      })()}
                      onChange={(_: any, record: any) => {
                        const doctor = record?.doctor as Doctor | undefined;
                        if (!doctor) return;
                        if (doctor.type === UserRoleEnum.ADMIN) {
                          setFieldValue("assignedToAdminId", doctor.id);
                          setFieldValue("assignedToDoctorId", undefined);
                        } else {
                          setFieldValue("assignedToDoctorId", doctor.id);
                          setFieldValue("assignedToAdminId", undefined);
                        }
                      }}
                      setFieldValue={setFieldValue}
                      options={displayDoctorOptions}
                    />
                  </Col>
                )}
              </Row>
              <div className="case-assignment-form__submit-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={formLoading}
                  disabled={
                    formLoading ||
                    (userRole !== UserRoleEnum.SCAN_CENTER &&
                      !values?.selfAssign &&
                      !values?.assignedToAdminId &&
                      !values?.assignedToDoctorId) ||
                    (userRole === UserRoleEnum.SCAN_CENTER &&
                      !values?.assignedToDoctorId && !values?.assignedToAdminId)
                  }
                >
                  Assign
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default AuthContainer(CaseAssignmentForm);
